import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, AlertTriangle, CalendarClock, Pencil, Plus, Eye } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { HubCard } from './HubMapCard';
import { fmtDate } from '../projects/projectUi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };
export const MILESTONES = [
  ['date_depot_pc', 'Dépôt PC'],
  ['date_ouverture_chantier', 'Ouverture chantier'],
  ['date_commercialisation', 'Commercialisation'],
  ['date_livraison', 'Livraison'],
];

const saveDates = async (projectId, dates) => axios.patch(`${API}/hub/milestones/${projectId}`, { dates }, opts);

/** Boîte « Jalons rapides » : choix du projet + 4 dates, sans passer par Paramètres. */
export const QuickMilestoneDialog = ({ open, onClose, projects, initialProjectId }) => {
  const [pid, setPid] = useState(initialProjectId || projects[0]?.project_id || '');
  const [dates, setDates] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setPid(initialProjectId || projects[0]?.project_id || ''); }, [open, initialProjectId, projects]);
  useEffect(() => {
    if (!open || !pid) return;
    axios.get(`${API}/hub/milestones/${pid}`, opts).then((r) => setDates(r.data.dates || {})).catch(() => setDates({}));
  }, [open, pid]);
  const submit = async () => {
    setBusy(true);
    try { await saveDates(pid, dates); toast.success('Jalons enregistrés'); onClose(true); } catch { toast.error('Enregistrement impossible'); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="sm:max-w-md" data-testid="quick-milestone-dialog">
        <DialogHeader><DialogTitle>Jalons rapides</DialogTitle><DialogDescription>Les dates alimentent les alertes du Hub et la frise côté client.</DialogDescription></DialogHeader>
        <div className="space-y-3 py-1">
          <label className="block text-xs font-medium text-[var(--c-text-2)]">Projet
            <select value={pid} onChange={(e) => setPid(e.target.value)} data-testid="quick-milestone-project" className="mt-1.5 w-full h-10 rounded-lg border border-[var(--c-border-strong)] bg-[var(--c-surface-0)] px-3 text-[13px] text-[var(--c-text)]">
              {projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            {MILESTONES.map(([k, label]) => (
              <label key={k} className="block text-xs font-medium text-[var(--c-text-2)]">{label}
                <input type="date" value={dates[k] || ''} onChange={(e) => setDates({ ...dates, [k]: e.target.value || null })} data-testid={`quick-milestone-${k}`} className="mt-1.5 w-full h-10 rounded-lg border border-[var(--c-border-strong)] bg-[var(--c-surface-0)] px-3 text-[13px] text-[var(--c-text)] tabular-nums" />
              </label>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)}>Annuler</Button>
          <button type="button" onClick={submit} disabled={busy || !pid} className="hud-btn hud-btn-primary hud-btn-md" data-testid="quick-milestone-save">Enregistrer</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AlertRow = ({ a, i, onChanged }) => {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(a.date || '');
  const isRender = a.key === 'renders_to_validate';
  const Icon = isRender ? CheckCircle2 : a.alert ? AlertTriangle : CalendarClock;
  const save = async (v) => {
    setEditing(false);
    if (v === (a.date || '')) return;
    try { await saveDates(a.project_id, { [a.key]: v || null }); toast.success(`${a.short} mis à jour`); onChanged(); } catch { toast.error('Enregistrement impossible'); }
  };
  return (
    <div data-testid={`hub-alert-${i}`} className={`flex items-start gap-3 px-3 py-2.5 rounded-xl border-l-2 hover:bg-[var(--c-surface-1)] ${a.alert ? 'border-red-400' : 'border-[var(--c-gold)]'}`}>
      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${a.alert ? 'text-red-500' : 'text-[var(--c-gold)]'}`} />
      <div className="min-w-0 flex-1">
        <p className="text-[13px] text-[var(--c-text)] truncate">{a.label}</p>
        <div className="flex items-center gap-2 text-[11px] text-[var(--c-text-muted)] min-w-0">
          <span className="truncate">{a.project_name}</span>
          {!isRender && (editing ? (
            <input autoFocus type="date" value={val} onChange={(e) => setVal(e.target.value)} onBlur={() => save(val)} onKeyDown={(e) => { if (e.key === 'Enter') save(val); if (e.key === 'Escape') setEditing(false); }} data-testid={`hub-alert-date-input-${i}`} className="h-6 rounded border border-[var(--c-gold)] bg-[var(--c-surface-0)] px-1.5 text-[11px] text-[var(--c-text)] tabular-nums" />
          ) : (
            <button type="button" onClick={() => { setVal(a.date || ''); setEditing(true); }} data-testid={`hub-alert-edit-${i}`} className="inline-flex items-center gap-1 tabular-nums hover:text-[var(--c-gold)]" title="Modifier la date">
              · {fmtDate(a.date)}{a.days_diff != null ? (a.days_diff < 0 ? ` · dépassé de ${-a.days_diff} j` : ` · dans ${a.days_diff} j`) : ''}<Pencil className="w-3 h-3" />
            </button>
          ))}
        </div>
      </div>
      {isRender ? (
        <button type="button" onClick={() => navigate(`/projects/${a.project_id}?section=production&tab=generation&review=1`)} data-testid={`hub-alert-review-${i}`} className="hud-btn hud-btn-primary hud-btn-sm shrink-0"><Eye className="w-3.5 h-3.5" />Valider maintenant</button>
      ) : (
        <button type="button" onClick={() => navigate(`/projects/${a.project_id}?section=settings`)} className="text-[11px] text-[var(--c-text-muted)] hover:text-[var(--c-gold)] shrink-0" data-testid={`hub-alert-open-${i}`}>Ouvrir</button>
      )}
    </div>
  );
};

export const HubAlertsCard = ({ data, projects, onChanged }) => {
  const [dlg, setDlg] = useState(false);
  return (
    <HubCard eyebrow="Alertes & jalons" title="À traiter" testid="hub-alerts-card" bodyClass="p-3" action={<button type="button" onClick={() => setDlg(true)} data-testid="hub-add-milestone-btn" className="inline-flex items-center gap-1 text-[11.5px] font-medium text-[var(--c-gold)] hover:underline"><Plus className="w-3.5 h-3.5" />Jalon</button>}>
      {!data ? <div className="h-24 rounded-lg bg-[var(--c-surface-2)] animate-pulse" /> : data.alerts.length === 0 ? (
        <p className="px-2 py-6 text-center text-[12.5px] text-[var(--c-text-muted)]">Aucune alerte. Ajoutez un jalon pour être prévenu 30 jours avant.</p>
      ) : data.alerts.map((a, i) => <AlertRow key={`${a.project_id}-${a.key}`} a={a} i={i} onChanged={onChanged} />)}
      <QuickMilestoneDialog open={dlg} onClose={(saved) => { setDlg(false); if (saved) onChanged(); }} projects={projects} />
    </HubCard>
  );
};
